import { INTL_TAG, type Locale } from './locales';

/*
 * All numbers / amounts / dates go through Intl for the current locale.
 * Amounts are integer tokens; `decimals: 2` is only for the header balance look ("12,480.00").
 */
const cache = new Map<string, Intl.NumberFormat>();
function nf(locale: Locale, opts: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `${locale}|${JSON.stringify(opts)}`;
  let f = cache.get(key);
  if (!f) {
    f = new Intl.NumberFormat(INTL_TAG[locale], opts);
    cache.set(key, f);
  }
  return f;
}

export interface TokenFormatOptions {
  decimals?: number;
  /** "+1,240" for positive (negative always gets "−", U+2212) */
  sign?: boolean;
  /** "12.4K" style for tight places */
  compact?: boolean;
}

export const MINUS = '−';

export function formatTokens(n: number, locale: Locale = 'en', o: TokenFormatOptions = {}): string {
  const d = o.decimals ?? 0;
  const f = o.compact
    ? nf(locale, { notation: 'compact', maximumFractionDigits: 1 })
    : nf(locale, { minimumFractionDigits: d, maximumFractionDigits: d });
  const s = f.format(Math.abs(n));
  if (n < 0) return MINUS + s;
  if (o.sign && n > 0) return '+' + s;
  return s;
}

/** 2.4712 → "2.47×" (de: "2,47×"). Always 2 decimals, truncated (never shows more than was reached). */
export function formatMultiplier(x: number, locale: Locale = 'en'): string {
  const t = Math.floor(x * 100 + 1e-9) / 100;
  return nf(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(t) + '×';
}

export function formatPercent(ratio: number, locale: Locale = 'en', decimals = 1): string {
  return nf(locale, {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(ratio);
}

export function formatInteger(n: number, locale: Locale = 'en'): string {
  return nf(locale, { maximumFractionDigits: 0 }).format(n);
}

export function formatDate(
  d: Date | string | number,
  locale: Locale = 'en',
  o: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
): string {
  return new Intl.DateTimeFormat(INTL_TAG[locale], o).format(new Date(d));
}

export function formatTime(d: Date | string | number, locale: Locale = 'en'): string {
  return new Intl.DateTimeFormat(INTL_TAG[locale], { hour: '2-digit', minute: '2-digit' }).format(new Date(d));
}

/** mm:ss (or hh:mm:ss) countdown — timers are mono digits, same in every language. */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const p = (v: number) => String(v).padStart(2, '0');
  return h > 0 ? `${p(h)}:${p(m)}:${p(s)}` : `${p(m)}:${p(s)}`;
}

/** Countdown of a day or more: whole days + the remainder as HH:MM (always hours, even when they are 0). */
export function splitDaysCountdown(ms: number): { days: number; time: string } {
  const totalMin = Math.max(0, Math.floor(ms / 60_000));
  const days = Math.floor(totalMin / 1440);
  const h = Math.floor((totalMin % 1440) / 60);
  const m = totalMin % 60;
  const p = (v: number) => String(v).padStart(2, '0');
  return { days, time: `${p(h)}:${p(m)}` };
}

/** Colour token for a crash multiplier (HANDOFF: <2× muted, 2–10× win colour — teal, red in cn — >10× orange). */
export function multiplierColor(x: number): string {
  return x < 2 ? 'var(--muted)' : x <= 10 ? 'var(--win)' : 'var(--orange-text)';
}
